import { useSearchParams, Link } from 'react-router-dom';
import { useState } from 'react';
import NewsNavbar from '@/components/feature/NewsNavbar';
import NewsFooter from '@/components/feature/NewsFooter';
import { useSearchNews, useCategories } from '@/hooks/useNews';
import { useSEO } from '@/hooks/useSEO';

function newsHref(item: { id: string; slug?: string }): string {
  return item.slug ? `/haber/${item.slug}` : `/haber/${item.id}`;
}

const CATEGORY_COLORS: Record<string, string> = {
  'yapay-zeka': '#00D9FF',
  'mobil': '#00FF88',
  'oyun': '#FF6B6B',
  'donanim': '#FFB347',
  'yazilim': '#A78BFA',
  'girisimcilik': '#34D399',
  'siber-guvenlik': '#FF4757',
  'kripto': '#F7931A',
  'otomotiv': '#2ED573',
  'bilim': '#5352ED',
};

export default function NewsSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const activeCategory = searchParams.get('cat') || '';
  const { news: results, loading } = useSearchNews(query);
  const { categories } = useCategories();
  const [inputVal, setInputVal] = useState(query);

  const activeCategory_obj = categories.find(c => c.id === activeCategory);
  const filtered = activeCategory
    ? results.filter(n => n.category === activeCategory)
    : results;

  const seoTitle = query
    ? activeCategory
      ? `"${query}" - ${activeCategory_obj?.name || activeCategory} Haberleri`
      : `"${query}" için Arama Sonuçları`
    : 'Teknoloji Haberi Ara';

  const seoDescription = query
    ? `TeknoNovaHub'da "${query}" için ${filtered.length} teknoloji haberi bulundu.${activeCategory ? ` ${activeCategory_obj?.name} kategorisinde filtrelendi.` : ''} Yapay zeka, yazılım, girişimcilik ve daha fazlası.`
    : 'TeknoNovaHub teknoloji haber arşivinde arama yapın. Yapay zeka, mobil, oyun, siber güvenlik, kripto ve daha fazla kategoride binlerce haber.';

  useSEO({
    title: seoTitle,
    description: seoDescription,
    keywords: query
      ? `${query}, teknoloji haberleri, TeknoNovaHub${activeCategory ? `, ${activeCategory_obj?.name}` : ''}`
      : 'teknoloji haberleri ara, yapay zeka, yazılım, girişimcilik, TeknoNovaHub',
    url: query ? `/arama?q=${encodeURIComponent(query)}` : '/arama',
    type: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SearchResultsPage',
      name: seoTitle,
      description: seoDescription,
      url: `https://teknonovahub.com/arama${query ? `?q=${encodeURIComponent(query)}` : ''}`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: 'https://teknonovahub.com' },
          { '@type': 'ListItem', position: 2, name: 'Arama', item: 'https://teknonovahub.com/arama' },
          ...(query ? [{ '@type': 'ListItem', position: 3, name: `"${query}"`, item: `https://teknonovahub.com/arama?q=${encodeURIComponent(query)}` }] : []),
        ],
      },
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (inputVal.trim()) params.q = inputVal.trim();
    if (activeCategory) params.cat = activeCategory;
    setSearchParams(params);
  };

  const toggleCategory = (catId: string) => {
    const params: Record<string, string> = {};
    if (query) params.q = query;
    if (activeCategory !== catId) params.cat = catId;
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] font-sans">
      <NewsNavbar />
      <main className="pt-24 md:pt-28">
        <div className="bg-white/3 border-b border-white/10">
          <div className="max-w-5xl mx-auto px-6 py-10">
            <nav className="flex items-center gap-2 text-sm text-white/40 mb-6">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">Ana Sayfa</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-white">Arama</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-6">
              {query ? (<><span className="text-[#00D9FF]">&ldquo;{query}&rdquo;</span> için sonuçlar</>) : 'Haber Ara'}
            </h1>
            <form onSubmit={handleSearch} className="flex gap-3 mb-6">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-lg" />
                <input
                  type="text"
                  value={inputVal}
                  onChange={e => setInputVal(e.target.value)}
                  placeholder="Haber, konu veya yazar ara..."
                  className="w-full bg-white/5 border border-white/15 rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#00D9FF]/50 focus:bg-white/8 transition-all"
                />
              </div>
              <button type="submit" className="px-6 py-3.5 bg-[#00D9FF] text-[#0A0E27] font-bold rounded-xl hover:bg-[#00D9FF]/90 transition-colors cursor-pointer whitespace-nowrap text-sm">Ara</button>
            </form>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => toggleCategory('')}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${!activeCategory ? 'bg-white text-[#0A0E27]' : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'}`}
              >
                Tümü
              </button>
              {categories.map(cat => {
                const color = CATEGORY_COLORS[cat.id] || '#00D9FF';
                const isActive = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border"
                    style={isActive
                      ? { backgroundColor: `${color}20`, color, borderColor: `${color}60` }
                      : { backgroundColor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.1)' }
                    }
                  >
                    <i className={`${cat.icon} text-xs`} />{cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10 pb-16">
          {!loading && query && (
            <p className="text-white/40 text-sm mb-6">
              {filtered.length > 0
                ? `${filtered.length} haber bulundu${activeCategory ? ` (${categories.find(c => c.id === activeCategory)?.name} kategorisinde)` : ''}`
                : 'Sonuç bulunamadı'}
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-white/5" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 bg-white/5 rounded w-1/4" />
                    <div className="h-4 bg-white/5 rounded w-3/4" />
                    <div className="h-3 bg-white/5 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item) => {
                const category = categories.find(c => c.id === item.category);
                const catColor = CATEGORY_COLORS[item.category] || '#00D9FF';
                return (
                  <Link key={item.id} to={newsHref(item)} className="group cursor-pointer block">
                    <div className="relative overflow-hidden rounded-2xl mb-4">
                      <div className="aspect-[16/10]">
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <span
                        className="absolute top-3 left-3 px-2.5 py-1 text-[10px] font-bold rounded-full"
                        style={{ backgroundColor: `${catColor}25`, color: catColor, border: `1px solid ${catColor}40` }}
                      >
                        {category?.name}
                      </span>
                    </div>
                    <h3 className="text-white font-bold text-base mb-2 line-clamp-2 group-hover:text-[#00D9FF] transition-colors leading-snug">{item.title}</h3>
                    <p className="text-white/50 text-sm line-clamp-2 mb-3 leading-relaxed">{item.excerpt}</p>
                    <div className="flex items-center gap-3 text-white/40 text-xs">
                      <span>{item.author}</span><span>·</span><span>{item.date}</span><span>·</span><span>{item.read_time} okuma</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : query ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center bg-white/5 rounded-full mx-auto mb-5">
                <i className="ri-search-2-line text-4xl text-white/20" />
              </div>
              <h3 className="text-white font-bold text-xl mb-2">Sonuç Bulunamadı</h3>
              <p className="text-white/40 mb-8 max-w-sm mx-auto">&ldquo;{query}&rdquo; için herhangi bir haber bulamadık. Farklı anahtar kelimeler deneyin.</p>
              <div className="flex flex-wrap justify-center gap-2">
                {['Yapay Zeka', 'Bitcoin', 'iPhone', 'GTA VI', 'Tesla'].map(s => (
                  <button key={s} onClick={() => { setInputVal(s); setSearchParams({ q: s }); }} className="px-4 py-2 bg-white/5 border border-white/10 text-white/60 text-sm rounded-full hover:bg-white/10 transition-colors cursor-pointer">{s}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-10">
              <h2 className="text-white/60 text-lg font-semibold mb-6">Kategoriye Göre Gözat</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {categories.map(cat => {
                  const color = CATEGORY_COLORS[cat.id] || '#00D9FF';
                  return (
                    <Link key={cat.id} to={`/kategori/${cat.id}`} className="group flex flex-col items-center gap-3 p-5 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/8 hover:border-white/20 transition-all cursor-pointer">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${color}15` }}>
                        <i className={`${cat.icon} text-xl`} style={{ color }} />
                      </div>
                      <span className="text-white/70 text-sm font-medium text-center group-hover:text-white transition-colors">{cat.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
      <NewsFooter />
    </div>
  );
}
